import { suppliersAPI, coreAPI, onboardingAPI } from '../../../lib/api';
import type { Site } from '../../../lib/api';
import type { SupplierFormData, ContactEntry, SiteEntry, SiteAccessConfig } from './types';
import { validateWizardStep } from './types';

interface LoadSupplierDataParams {
  id: string;
  setLoading: (v: boolean) => void;
  setCustomerSites: (v: Site[]) => void;
  setSupplierStatus: (v: string) => void;
  setPreviewMode: (v: boolean) => void;
  setFormData: (v: SupplierFormData) => void;
  setCurrentStep: (v: number | ((prev: number) => number)) => void;
  setHighestCompletedStep: (v: number) => void;
  setWorkflowId: (v: number | null) => void;
  /** If set, use this step instead of the draft's saved current_step */
  urlStepOverride?: number;
}

/**
 * Loads an existing supplier's data from the API and populates the wizard form state.
 * Handles restoring from wizard_draft if available, or falling back to field-by-field mapping.
 *
 * @param params - All setter functions needed to populate wizard state
 */
export async function loadSupplierData(params: LoadSupplierDataParams): Promise<void> {
  const {
    id,
    setLoading,
    setCustomerSites,
    setSupplierStatus,
    setPreviewMode,
    setFormData,
    setCurrentStep,
    setHighestCompletedStep,
    setWorkflowId,
    urlStepOverride,
  } = params;

  try {
    setLoading(true);
    const response = await suppliersAPI.getProfile(Number(id));
    const supplier = response.data;

    const companyDetails = supplier.company_details as unknown as Record<string, unknown>;
    const supplierRecord = supplier as unknown as Record<string, unknown>;

    // Determine group membership from company details
    const hasGroup = !!companyDetails?.['group'];
    const groupId = companyDetails?.['group'] as number | undefined;
    const groupName = companyDetails?.['group_name'] as string | undefined;

    // Restore linked customer sites from supplier-customer relationships
    let customerSiteIds: number[] = [];
    let customerId: number | undefined;
    let customerName = '';
    try {
      const [relRes, sitesRes] = await Promise.all([
        coreAPI.getSupplierRelationships({ supplier: supplier.company, page_size: 100 }),
        coreAPI.getAccessibleSites(),
      ]);
      const relPayload = relRes.data as { results?: Array<{ is_active?: boolean; customer: number }> } | Array<{ is_active?: boolean; customer: number }>;
      const relResults = Array.isArray(relPayload) ? relPayload : (relPayload.results ?? []);
      customerSiteIds = relResults
        .filter((r) => r.is_active !== false)
        .map((r) => r.customer);

      const sitesPayload = sitesRes.data;
      const sitesList: Site[] = Array.isArray(sitesPayload)
        ? sitesPayload
        : ((sitesPayload as { results?: Site[] }).results ?? []);
      setCustomerSites(sitesList);

      if (customerSiteIds.length > 0) {
        const firstSite = sitesList.find((s) => s.id === customerSiteIds[0]);
        if (firstSite) {
          // CR-4836: Company IS the site on this platform (commit d9678626),
          // and the accessible-sites payload no longer includes a separate
          // `company` field — it's the same as `id`. Reading `firstSite.company`
          // returned undefined, so when a draft resumed without a persisted
          // `customer_id` in wizard_draft the wizard had no customer, and the
          // final submit-wizard POST dropped customer_id and hit a 400
          // ("supplier_id and customer_id are required") surfaced as
          // "Failed to save. Please try again." in the toast.
          customerId = firstSite.id;
          customerName = firstSite.name;
        }
      }
    } catch (relErr) {
      console.error('Failed to load supplier customer sites:', relErr);
    }

    // Set status from onboarding_status field
    const onboardingStatus = (supplier as unknown as Record<string, unknown>)['onboarding_status'] as string;
    setSupplierStatus(onboardingStatus || (supplier.is_approved ? 'approved' : 'draft'));

    // Only lock fields for fully approved suppliers
    const isFullyApproved = onboardingStatus === 'approved' || (!onboardingStatus && supplier.is_approved);
    setPreviewMode(isFullyApproved);

    // Restore from wizard_draft if available
    const draft = supplier.wizard_draft;
    if (draft?.form_data && typeof draft.form_data === 'object' && Object.keys(draft.form_data).length > 0) {
      const draftForm = draft.form_data as Record<string, unknown>;
      setFormData({
        is_part_of_group: draftForm['is_part_of_group'] as boolean | null ?? null,
        supplier_group_id: draftForm['supplier_group_id'] as number | undefined,
        supplier_group_name: draftForm['supplier_group_name'] as string | undefined,
        company_name: (draftForm['company_name'] as string) || supplier.company_name || '',
        company_address: (draftForm['company_address'] as string) || '',
        // CR-5390: structured address fields. Fall back to the legacy single-line
        // address when restoring a draft submitted before the structured form
        // landed, so users don't lose their typed value.
        address_line_1: (draftForm['address_line_1'] as string) || (draftForm['company_address'] as string) || '',
        address_line_2: (draftForm['address_line_2'] as string) || '',
        address_city: (draftForm['address_city'] as string) || '',
        address_region: (draftForm['address_region'] as string) || '',
        address_postcode: (draftForm['address_postcode'] as string) || '',
        address_country: (draftForm['address_country'] as string) || '',
        currency: (draftForm['currency'] as string) || supplier.currency || 'GBP',
        annual_spend: (draftForm['annual_spend'] as string) || '',
        description: (draftForm['description'] as string) || '',
        customer_id: (draftForm['customer_id'] as number | undefined) ?? customerId,
        customer_name: (draftForm['customer_name'] as string) || customerName,
        customer_ids: (draftForm['customer_ids'] as number[]) || customerSiteIds,
        sites: (draftForm['sites'] as SiteEntry[]) || [],
        contact_title: (draftForm['contact_title'] as string) || '',
        contact_first_name: (draftForm['contact_first_name'] as string) || '',
        contact_last_name: (draftForm['contact_last_name'] as string) || '',
        contact_email: (draftForm['contact_email'] as string) || '',
        contact_phone: (draftForm['contact_phone'] as string) || '',
        contact_phone_extension: (draftForm['contact_phone_extension'] as string) || '',
        contact_job_title: (draftForm['contact_job_title'] as string) || '',
        contact_department: (draftForm['contact_department'] as string) || '',
        contact_login_type: (draftForm['contact_login_type'] as string) || 'supplier',
        contact_linkedin: (draftForm['contact_linkedin'] as string) || '',
        additional_contacts: (draftForm['additional_contacts'] as ContactEntry[]) || [],
        related_companies: (draftForm['related_companies'] as Array<{ name: string; address: string; type: string }>) || [],
        site_access: (draftForm['site_access'] as Record<string, SiteAccessConfig>) || undefined,
        risk_answers: (draftForm['risk_answers'] as Record<string, string>) || {},
        logo_url: draftForm['logo_url'] as string | undefined,
      });
      // Restore step progress from draft, but re-validate to compute actual completion
      // This ensures steps with missing data don't show as completed
      const restoredForm: SupplierFormData = {
        is_part_of_group: draftForm['is_part_of_group'] as boolean | null ?? null,
        supplier_group_id: draftForm['supplier_group_id'] as number | undefined,
        company_name: (draftForm['company_name'] as string) || supplier.company_name || '',
        currency: (draftForm['currency'] as string) || supplier.currency || 'GBP',
        annual_spend: (draftForm['annual_spend'] as string) || '',
        customer_id: (draftForm['customer_id'] as number | undefined) ?? customerId,
        customer_ids: (draftForm['customer_ids'] as number[]) || customerSiteIds,
        contact_first_name: (draftForm['contact_first_name'] as string) || '',
        contact_last_name: (draftForm['contact_last_name'] as string) || '',
        contact_email: (draftForm['contact_email'] as string) || '',
        additional_contacts: (draftForm['additional_contacts'] as ContactEntry[]) || [],
        company_address: (draftForm['company_address'] as string) || '',
        // CR-5390: structured address (mirror of the setFormData call above).
        address_line_1: (draftForm['address_line_1'] as string) || (draftForm['company_address'] as string) || '',
        address_line_2: (draftForm['address_line_2'] as string) || '',
        address_city: (draftForm['address_city'] as string) || '',
        address_region: (draftForm['address_region'] as string) || '',
        address_postcode: (draftForm['address_postcode'] as string) || '',
        address_country: (draftForm['address_country'] as string) || '',
        description: (draftForm['description'] as string) || '',
        customer_name: (draftForm['customer_name'] as string) || customerName,
        sites: (draftForm['sites'] as SiteEntry[]) || [],
        contact_title: (draftForm['contact_title'] as string) || '',
        contact_phone: (draftForm['contact_phone'] as string) || '',
        contact_phone_extension: (draftForm['contact_phone_extension'] as string) || '',
        contact_job_title: (draftForm['contact_job_title'] as string) || '',
        contact_department: (draftForm['contact_department'] as string) || '',
        contact_login_type: (draftForm['contact_login_type'] as string) || 'supplier',
        contact_linkedin: (draftForm['contact_linkedin'] as string) || '',
        related_companies: (draftForm['related_companies'] as Array<{ name: string; address: string; type: string }>) || [],
        site_access: (draftForm['site_access'] as Record<string, SiteAccessConfig>) || undefined,
        risk_answers: (draftForm['risk_answers'] as Record<string, string>) || {},
      };

      // Re-validate each step to find the actual highest *valid* completed step
      let validatedHighest = 0;
      for (let step = 1; step <= 5; step++) {
        if (validateWizardStep(step, restoredForm) === null) {
          validatedHighest = step;
        } else {
          break; // Stop at first invalid step
        }
      }

      // Use the validated highest, but allow navigation up to the draft's highest
      // so the user can still access steps they previously visited (even if now invalid)
      const navigableHighest = Math.max(
        validatedHighest,
        typeof draft.highest_step === 'number' ? draft.highest_step : 0
      );

      // URL step param takes priority over draft's saved step
      if (urlStepOverride && urlStepOverride > 0) {
        setCurrentStep(urlStepOverride);
      } else if (typeof draft.current_step === 'number') {
        setCurrentStep(draft.current_step);
      }
      setHighestCompletedStep(navigableHighest);
    } else {
      // No draft - fall back to field-by-field mapping from API data
      setFormData({
        is_part_of_group: hasGroup ? true : (supplier.company_name ? false : null),
        supplier_group_id: hasGroup && groupId ? groupId : undefined,
        supplier_group_name: hasGroup && groupName ? groupName : undefined,
        company_name: supplier.company_name || '',
        company_address: (companyDetails?.['address_line_1'] as string) || '',
        // CR-5390: hydrate structured address from the company record.
        address_line_1: (companyDetails?.['address_line_1'] as string) || '',
        address_line_2: (companyDetails?.['address_line_2'] as string) || '',
        address_city: (companyDetails?.['city'] as string) || '',
        address_region: (companyDetails?.['state'] as string) || '',
        address_postcode: (companyDetails?.['postal_code'] as string) || '',
        address_country: (companyDetails?.['country'] as string) || '',
        currency: supplier.currency || (companyDetails?.['currency'] as string) || 'GBP',
        annual_spend: supplier.annual_revenue ? String(supplier.annual_revenue) : '',
        description: (companyDetails?.['description'] as string) || '',
        customer_id: customerId,
        customer_name: customerName,
        customer_ids: customerSiteIds,
        sites: [],
        contact_title: (supplierRecord['contact_title'] as string) || '',
        contact_first_name: (supplierRecord['contact_first_name'] as string) || '',
        contact_last_name: (supplierRecord['contact_last_name'] as string) || '',
        contact_email: (supplierRecord['contact_email'] as string) || '',
        contact_phone: (supplierRecord['contact_phone'] as string) || '',
        contact_phone_extension: (supplierRecord['contact_phone_extension'] as string) || '',
        contact_job_title: (supplierRecord['contact_job_title'] as string) || '',
        contact_department: (supplierRecord['contact_department'] as string) || '',
        contact_login_type: (supplierRecord['contact_login_type'] as string) || 'supplier',
        contact_linkedin: (supplierRecord['contact_linkedin'] as string) || '',
        additional_contacts: [],
        related_companies: [],
        risk_answers: {},
        logo_url: companyDetails?.['logo_url'] as string | undefined,
      });
    }

    // Determine how far the wizard was completed (only if no draft restored step info)
    if (!draft?.form_data || !draft.highest_step) {
      if (onboardingStatus && onboardingStatus !== 'draft') {
        setHighestCompletedStep(5);
      } else {
        // Infer wizard progress from saved fields only. Do NOT infer step 4 from
        // customerSiteIds — that's a step-2 relationship, unrelated to per-contact
        // site access which is what step 4 ("Supplier User Related Companies") asks.
        let completed = 0;
        if (supplier.company_name) completed = 1;
        if (supplier.company_name && (companyDetails?.['address'] || companyDetails?.['description'])) completed = 2;
        if (supplierRecord['contact_email'] || supplierRecord['contact_first_name']) completed = 3;
        setHighestCompletedStep(completed);
      }
    }

    // Try to load existing workflow for this supplier
    try {
      const workflowsResponse = await onboardingAPI.getWorkflows({ supplier: Number(id), page_size: 1 });
      const workflows = workflowsResponse.data.results || [];
      if (workflows.length > 0) {
        setWorkflowId(workflows[0].id);
      }
    } catch {
      // No workflow yet, that's fine
    }
  } catch (error) {
    console.error('Failed to load supplier:', error);
  } finally {
    setLoading(false);
  }
}
